import { useState, type FormEvent } from 'react';
import { Dialog } from './Dialog';
import { Button } from './Button';
import { InputPassword } from './InputPassword';
import { FloatingField } from './FloatingField';

export interface ChangePasswordDialogProps {
  /** ダイアログが開いているか */
  open: boolean;
  /** 閉じる時のコールバック */
  onClose: () => void;
  /** 成功時のコールバック */
  onSuccess?: () => void;
}

interface FieldErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

/**
 * パスワード変更ダイアログコンポーネント
 */
export const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  /**
   * フォームをリセット
   */
  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setFieldErrors({});
  };

  /**
   * ダイアログを閉じる
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  /**
   * フォームのバリデーション
   */
  const validateForm = (): boolean => {
    const errors: FieldErrors = {};

    if (!currentPassword) {
      errors.currentPassword = '現在のパスワードを入力してください';
    }

    if (!newPassword) {
      errors.newPassword = '新しいパスワードを入力してください';
    } else if (newPassword.length < 8) {
      errors.newPassword = 'パスワードは8文字以上で入力してください';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'パスワード（確認）を入力してください';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'パスワードが一致しません';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length > 0;
  };

  /**
   * フィールドのエラーをクリア
   */
  const clearFieldError = (field: keyof FieldErrors) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  /**
   * フォーム送信
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('auth_access_token');
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        resetForm();
        onSuccess?.();
        onClose();
      } else {
        setError(data.message || 'パスワードの変更に失敗しました');
      }
    } catch {
      setError('通信エラーが発生しました。しばらく経ってから再度お試しください。');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="パスワードの変更"
      type="modal"
      footer={
        <>
          <Button
            buttonType="outline"
            size="md"
            onClick={handleClose}
            disabled={isLoading}
          >
            キャンセル
          </Button>
          <Button
            buttonType="solid-fill"
            size="md"
            onClick={handleSubmit}
            disabled={isLoading || !currentPassword || !newPassword || !confirmPassword}
          >
            {isLoading ? '変更中...' : '変更する'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        {error && (
          <div
            style={{
              padding: '0.75rem 1rem',
              backgroundColor: 'var(--color-red50)',
              color: 'var(--color-alert)',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* 現在のパスワード */}
          <FloatingField
            label="現在のパスワード"
            htmlFor="currentPassword"
            required
            errorText={fieldErrors.currentPassword}
            hasError={!!fieldErrors.currentPassword}
          >
            <InputPassword
              id="currentPassword"
              name="currentPassword"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                clearFieldError('currentPassword');
              }}
              autoComplete="current-password"
              aria-invalid={!!fieldErrors.currentPassword}
            />
          </FloatingField>

          {/* 新しいパスワード */}
          <FloatingField
            label="新しいパスワード"
            htmlFor="newPassword"
            required
            supportText="8文字以上で入力してください"
            errorText={fieldErrors.newPassword}
            hasError={!!fieldErrors.newPassword}
          >
            <InputPassword
              id="newPassword"
              name="newPassword"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                clearFieldError('newPassword');
              }}
              autoComplete="new-password"
              aria-invalid={!!fieldErrors.newPassword}
            />
          </FloatingField>

          {/* 新しいパスワード（確認） */}
          <FloatingField
            label="新しいパスワード（確認）"
            htmlFor="confirmPassword"
            required
            errorText={fieldErrors.confirmPassword}
            hasError={!!fieldErrors.confirmPassword}
          >
            <InputPassword
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                clearFieldError('confirmPassword');
              }}
              autoComplete="new-password"
              aria-invalid={!!fieldErrors.confirmPassword}
            />
          </FloatingField>
        </div>
      </form>
    </Dialog>
  );
};

export default ChangePasswordDialog;

